/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { JSX, useState, useEffect, useMemo, useCallback } from 'react';
import { UpdatePage } from '@/components/molecules';
import { TextFieldElement } from 'react-hook-form-mui';
import {
  Box,
  Card,
  CardHeader,
  Checkbox,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  OutlinedInput,
  Stack,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import GroupAPI from '@apps/accounts/services/groups';
import PermissionsAPI from '@apps/accounts/services/permissions';
import { ModelProps } from '@/types';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { GroupsSchema, GroupsSchemaType } from '@apps/accounts/schemas/groups';
import { AxiosError, AxiosResponse } from 'axios';

// Tipos para as permissões
interface Permission {
  id: number;
  label: string;
  description?: string;
}

interface PermissionListProps {
  title: string;
  permissions: Permission[];
  selectedIds: Set<number>;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onTogglePermission: (permission: Permission) => void;
  onToggleAll: () => void;
}

// Componente para lista de permissões
const PermissionList: React.FC<PermissionListProps> = ({
  title,
  permissions,
  selectedIds,
  searchTerm,
  onSearchChange,
  onTogglePermission,
  onToggleAll,
}) => {
  const { t } = useTranslation();

  const filteredPermissions = useMemo(
    () =>
      permissions.filter((permission) =>
        permission.label.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [permissions, searchTerm],
  );

  const selectedCount = useMemo(
    () =>
      filteredPermissions.filter((permission) => selectedIds.has(permission.id))
        .length,
    [filteredPermissions, selectedIds],
  );

  const allSelected =
    selectedCount === filteredPermissions.length &&
    filteredPermissions.length > 0;
  const indeterminate =
    selectedCount > 0 && selectedCount < filteredPermissions.length;

  return (
    <Card sx={{ height: 500, display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title={title}
        subheader={`${selectedCount}/${filteredPermissions.length} ${t('Selected')}`}
        action={
          <Checkbox
            checked={allSelected}
            indeterminate={indeterminate}
            onChange={onToggleAll}
            disabled={filteredPermissions.length === 0}
          />
        }
        sx={{ pb: 1 }}
      />

      <Box sx={{ px: 2, pb: 1 }}>
        <FormControl fullWidth size="small">
          <OutlinedInput
            placeholder={t('Search')}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            }
          />
        </FormControl>
      </Box>

      <Divider />

      <List
        sx={{
          flex: 1,
          overflow: 'auto',
          py: 0,
        }}
        dense
      >
        {filteredPermissions.map((permission) => (
          <ListItem key={permission.id} disablePadding>
            <ListItemButton
              onClick={() => onTogglePermission(permission)}
              dense
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox
                  edge="start"
                  checked={selectedIds.has(permission.id)}
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText
                primary={permission.label}
                secondary={permission.description}
              />
            </ListItemButton>
          </ListItem>
        ))}
        {filteredPermissions.length === 0 && (
          <ListItem>
            <ListItemText
              primary={t('No items found')}
              sx={{ textAlign: 'center', color: 'text.secondary' }}
            />
          </ListItem>
        )}
      </List>
    </Card>
  );
};

// Componente para os botões de transferência
const TransferButtons: React.FC<{
  onMoveToSelected: () => void;
  onMoveToAvailable: () => void;
  canMoveToSelected: boolean;
  canMoveToAvailable: boolean;
}> = ({
  onMoveToSelected,
  onMoveToAvailable,
  canMoveToSelected,
  canMoveToAvailable,
}) => {
  const { t } = useTranslation();

  return (
    <Stack
      spacing={2}
      alignItems="center"
      justifyContent="center"
      sx={{ minHeight: 400 }}
    >
      <Tooltip title={t('Move selected to chosen')}>
        <IconButton
          onClick={onMoveToSelected}
          disabled={!canMoveToSelected}
          size="large"
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': { bgcolor: 'primary.dark' },
            '&:disabled': { bgcolor: 'grey.300' },
          }}
        >
          <ArrowForwardIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title={t('Move selected to available')}>
        <IconButton
          onClick={onMoveToAvailable}
          disabled={!canMoveToAvailable}
          size="large"
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': { bgcolor: 'primary.dark' },
            '&:disabled': { bgcolor: 'grey.300' },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

// Componente principal
export default function GroupUpdatePage(modelProps: ModelProps): JSX.Element {
  const { t } = useTranslation();
  const formContext = useForm<GroupsSchemaType>();

  // Estados principais
  const [availablePermissions, setAvailablePermissions] = useState<
    Permission[]
  >([]);
  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>(
    [],
  );
  const [availableSelection, setAvailableSelection] = useState<Set<number>>(
    new Set(),
  );
  const [selectedSelection, setSelectedSelection] = useState<Set<number>>(
    new Set(),
  );
  const [availableSearch, setAvailableSearch] = useState('');
  const [selectedSearch, setSelectedSearch] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  // Query para buscar permissões
  const { data: permissionsResponse, isLoading } = useQuery<
    AxiosResponse<Permission[]>,
    AxiosError
  >({
    queryKey: ['permissions'],
    queryFn: () => PermissionsAPI.select({}),
  });

  // Inicializar permissões disponíveis
  useEffect(() => {
    if (permissionsResponse?.data && !isLoading && !isInitialized) {
      // Aguardar um pouco para que o handleEditing seja chamado primeiro
      const timer = setTimeout(() => {
        // Se não há permissões selecionadas ainda, definir todas como disponíveis
        if (selectedPermissions.length === 0) {
          setAvailablePermissions(permissionsResponse.data);
        }
        setIsInitialized(true);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [
    permissionsResponse,
    isLoading,
    selectedPermissions.length,
    isInitialized,
  ]);

  // Função para lidar com edição de grupo existente
  const handleEditing = useCallback(
    (data: any) => {
      if (data?.permissions && permissionsResponse?.data) {
        const existingPermissions = data.permissions as Permission[];
        const allPermissions = permissionsResponse.data;

        // Debug para verificar os dados
        console.log('Editing group with permissions:', existingPermissions);
        console.log('All available permissions:', allPermissions);

        // Separar permissões selecionadas das disponíveis
        const available = allPermissions.filter(
          (perm) =>
            !existingPermissions.some((existing) => existing.id === perm.id),
        );

        console.log('Available after filtering:', available);
        console.log('Selected permissions:', existingPermissions);

        // Atualizar os estados das listas
        setAvailablePermissions(available);
        setSelectedPermissions(existingPermissions);

        // Limpar as seleções temporárias
        setAvailableSelection(new Set());
        setSelectedSelection(new Set());
        setIsInitialized(true);
      } else if (permissionsResponse?.data) {
        // Caso seja um novo grupo, mostrar todas as permissões como disponíveis
        setAvailablePermissions(permissionsResponse.data);
        setSelectedPermissions([]);
        setAvailableSelection(new Set());
        setSelectedSelection(new Set());
        setIsInitialized(true);
      }
    },
    [permissionsResponse?.data],
  );

  // Atualizar valor do formulário quando permissões selecionadas mudarem
  useEffect(() => {
    formContext.setValue('permissions', selectedPermissions);
  }, [selectedPermissions, formContext]);

  // Handlers para seleção individual
  const handleToggleAvailablePermission = useCallback(
    (permission: Permission) => {
      setAvailableSelection((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(permission.id)) {
          newSet.delete(permission.id);
        } else {
          newSet.add(permission.id);
        }
        return newSet;
      });
    },
    [],
  );

  const handleToggleSelectedPermission = useCallback(
    (permission: Permission) => {
      setSelectedSelection((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(permission.id)) {
          newSet.delete(permission.id);
        } else {
          newSet.add(permission.id);
        }
        return newSet;
      });
    },
    [],
  );

  // Handlers para selecionar/deselecionar todos
  const handleToggleAllAvailable = useCallback(() => {
    const filteredPermissions = availablePermissions.filter((p) =>
      p.label.toLowerCase().includes(availableSearch.toLowerCase()),
    );

    const allSelected = filteredPermissions.every((p) =>
      availableSelection.has(p.id),
    );

    setAvailableSelection((prev) => {
      const newSet = new Set(prev);
      if (allSelected) {
        // Deselecionar todos os filtrados
        filteredPermissions.forEach((p) => newSet.delete(p.id));
      } else {
        // Selecionar todos os filtrados
        filteredPermissions.forEach((p) => newSet.add(p.id));
      }
      return newSet;
    });
  }, [availablePermissions, availableSearch, availableSelection]);

  const handleToggleAllSelected = useCallback(() => {
    const filteredPermissions = selectedPermissions.filter((p) =>
      p.label.toLowerCase().includes(selectedSearch.toLowerCase()),
    );

    const allSelected = filteredPermissions.every((p) =>
      selectedSelection.has(p.id),
    );

    setSelectedSelection((prev) => {
      const newSet = new Set(prev);
      if (allSelected) {
        // Deselecionar todos os filtrados
        filteredPermissions.forEach((p) => newSet.delete(p.id));
      } else {
        // Selecionar todos os filtrados
        filteredPermissions.forEach((p) => newSet.add(p.id));
      }
      return newSet;
    });
  }, [selectedPermissions, selectedSearch, selectedSelection]);

  // Handlers para transferir permissões
  const handleMoveToSelected = useCallback(() => {
    const permissionsToMove = availablePermissions.filter((p) =>
      availableSelection.has(p.id),
    );

    setAvailablePermissions((prev) =>
      prev.filter((p) => !availableSelection.has(p.id)),
    );
    setSelectedPermissions((prev) => [...prev, ...permissionsToMove]);
    setAvailableSelection(new Set());
  }, [availablePermissions, availableSelection]);

  const handleMoveToAvailable = useCallback(() => {
    const permissionsToMove = selectedPermissions.filter((p) =>
      selectedSelection.has(p.id),
    );

    setSelectedPermissions((prev) =>
      prev.filter((p) => !selectedSelection.has(p.id)),
    );
    setAvailablePermissions((prev) => [...prev, ...permissionsToMove]);
    setSelectedSelection(new Set());
  }, [selectedPermissions, selectedSelection]);

  // Loading state
  if (isLoading || !isInitialized) {
    return (
      <UpdatePage
        api={GroupAPI}
        modelProps={modelProps}
        formcontext={formContext}
        onEditing={handleEditing}
        schema={GroupsSchema}
        title={t('Groups')}
        customPath={'account/group'}
      >
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextFieldElement
              name="name"
              label={t('Name')}
              fullWidth
              required
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('Permissions')}
              </Typography>
              <Box
                sx={{
                  height: 500,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography color="text.secondary">
                  {t('Loading permissions...')}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </UpdatePage>
    );
  }

  return (
    <UpdatePage
      api={GroupAPI}
      modelProps={modelProps}
      formcontext={formContext}
      onEditing={handleEditing}
      schema={GroupsSchema}
      title={t('Groups')}
      customPath={'account/group'}
    >
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextFieldElement name="name" label={t('Name')} fullWidth required />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('Permissions')}
            </Typography>

            <Grid container spacing={2} alignItems="stretch">
              <Grid size={{ xs: 12, md: 5 }}>
                <PermissionList
                  title={t('Available Permissions')}
                  permissions={availablePermissions}
                  selectedIds={availableSelection}
                  searchTerm={availableSearch}
                  onSearchChange={setAvailableSearch}
                  onTogglePermission={handleToggleAvailablePermission}
                  onToggleAll={handleToggleAllAvailable}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <TransferButtons
                  onMoveToSelected={handleMoveToSelected}
                  onMoveToAvailable={handleMoveToAvailable}
                  canMoveToSelected={availableSelection.size > 0}
                  canMoveToAvailable={selectedSelection.size > 0}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 5 }}>
                <PermissionList
                  title={t('Selected Permissions')}
                  permissions={selectedPermissions}
                  selectedIds={selectedSelection}
                  searchTerm={selectedSearch}
                  onSearchChange={setSelectedSearch}
                  onTogglePermission={handleToggleSelectedPermission}
                  onToggleAll={handleToggleAllSelected}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </UpdatePage>
  );
}
